import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Backdrop, Box, Button, CircularProgress, IconButton, SvgIcon, Typography } from '@material-ui/core';
import { DataSpaceViewContext } from '../../my_dataspaces/context/DataSpaceViewContext';
import { UserProfileContext } from '../../../UserProfileContext';
import getDataSpaceImageLink from '../../../utils/getDataSpaceImageLink';
import getDataSpaceLogo from '../../../utils/getDataSpaceLogo';
import TwitterIcon from '@material-ui/icons/Twitter';
import WebsiteIcon from '@material-ui/icons/Language';
import RedditIcon from '@material-ui/icons/Reddit';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import { ReactComponent as TikTokIcon } from '../../../assets/icons/tiktok.svg';
import DefaultProfilePng from '../../../assets/img/DefaultProfile.png';
import { trackEvent } from '../../../utils/eventTracking';
import AuthModal from '../../ui_elements/AuthModal';
import { getUserProfile, cloneDataSpace } from '../../../api_helper/api';
import SelectFantasyLeagueModal from '../../ui_elements/SelectFantasyLeagueModal';
import MyDataSpacesViewContext from '../../my_dataspaces/context/MyDataSpacesViewContext';
import PageContext from '../../../context/PageContext';
import SnackbarAlertContext from '../../../context/SnackbarAlertContext';

const DataSpaceViewHeader = () => {
	const dataSpaceViewContext = useContext(DataSpaceViewContext);
	const myDataSpacesViewContext = useContext(MyDataSpacesViewContext);
	const pageContext = useContext(PageContext);
	const [userProfile, setUserProfile] = useContext(UserProfileContext);
	const snackbarAlertContext = useContext(SnackbarAlertContext);

	let author = dataSpaceViewContext.dataSpace.originalAuthor || dataSpaceViewContext.dataSpace.createdBy;

	let history = useHistory();

	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
	const [isSelectFantasyLeagueModalOpen, setIsSelectFantasyLeagueModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const handleAuth = () => {
		if (!localStorage.getItem('jwt')) {
			setIsAuthModalOpen(true);
			return;
		}
		handleAuthSuccess(userProfile);
	};

	const handleAuthSuccess = async () => {
		const fantasyLeagueDataset = dataSpaceViewContext.dataSpace.datasets.find((dataset) =>
			['Yahoo! Fantasy Sports', 'Fantrax'].includes(dataset.tags.api),
		);

		if (fantasyLeagueDataset) {
			setIsSelectFantasyLeagueModalOpen(true);
			return;
		}

		await handleCloneDataSpace();
	};

	const handleSelectFantasyLeagueSuccess = async (fantasyLeague) => {
		setIsSelectFantasyLeagueModalOpen(false);
		await handleCloneDataSpace(fantasyLeague);
	};

	const handleCloneDataSpace = async (fantasyLeague) => {
		try {
			setIsLoading(true);

			const res = await cloneDataSpace({
				dataSpaceId: dataSpaceViewContext.dataSpace._id,
				userId: userProfile._id,
				fantasyLeagueId: fantasyLeague?._id,
			});

			pageContext.setPage('mydataspaces');
			myDataSpacesViewContext.setMyDataSpacesView((prevState) => ({
				...prevState,
				selectedDataSpace: { name: res.data.name, id: res.data.dataSpaceId },
			}));
			myDataSpacesViewContext.setSelectedDataSpace({ name: res.data.name, id: res.data.dataSpaceId });
			myDataSpacesViewContext.changeViewHandler('dataSpaceViewNew');
			snackbarAlertContext.setSnackbarAlert({
				msg: 'DataSpace successfully added to your account',
				severity: 'success',
			});
			history.push('/app');

			trackEvent({
				userDetails: { userId: userProfile?._id, email: userProfile?.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User added shared DataSpace to their account.',
				},
			});
		} catch (error) {
			if (error.response?.data === 'DataSpace is not a Community DataSpace') {
				pageContext.setPage('CommunityDataSpaces');
				snackbarAlertContext.setSnackbarAlert({
					msg: 'This DataSpace does not have the permissions to be added to your account. Please see some of our Community DataSpaces below.',
					severity: 'error',
					autoHide: 8000,
				});
				history.push('/app');

				trackEvent({
					userDetails: { userId: userProfile?._id, email: userProfile?.email },
					eventDetails: {
						types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
						eventName:
							'WebApp_User attempted to add non-Community DataSpace to their account and was redirected to Community DataSpaces page.',
					},
				});
				return;
			}
			snackbarAlertContext.setSnackbarAlert({
				msg: 'There was an error adding the DataSpace to your account',
				severity: 'error',
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Backdrop open={isLoading} sx={{ width: '100vw', height: '100vh', zIndex: '10000', color: '#FFFFFF' }}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<AuthModal
				isOpen={isAuthModalOpen}
				onClose={() => setIsAuthModalOpen(false)}
				onSuccess={async (result) => {
					localStorage.setItem('jwt', result.token);
					const user = await getUserProfile();
					setUserProfile(user);
					setIsAuthModalOpen(false);
					handleAuthSuccess(user);
				}}
			/>

			<SelectFantasyLeagueModal
				isOpen={isSelectFantasyLeagueModalOpen}
				onClose={() => setIsSelectFantasyLeagueModalOpen(false)}
				onSuccess={handleSelectFantasyLeagueSuccess}
			/>

			<div
				style={{
					padding: '0.5rem',
					backgroundColor: '#47A8BD',
					position: 'relative',
				}}
			>
				<img
					src={
						dataSpaceViewContext.dataSpace.dataSpaceImageUrl
							? dataSpaceViewContext.dataSpace.dataSpaceImageUrl
							: getDataSpaceImageLink(dataSpaceViewContext.dataSpace)
					}
					style={{
						maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 50%)',
						position: 'absolute',
						top: 0,
						right: 0,
						height: '100%',
						zIndex: '1',
					}}
				/>
				<div style={{ width: '100%', display: 'flex', alignItems: 'center', position: 'relative', zIndex: '2' }}>
					<img
						src={getDataSpaceLogo(dataSpaceViewContext.dataSpace)}
						alt="DataSpace Logo"
						style={{
							width: '3rem',
							height: '3rem',
							marginRight: '0.5rem',
							borderRadius: '0.25rem',
							border: '1px solid white',
						}}
					/>

					<Box
						sx={{
							backgroundColor: 'rgba(255, 255, 255, 0.8)',
							marginRight: 'auto',
							padding: '0.25rem 0.5rem',
							borderRadius: '0.5rem',
							display: 'none',
							flexWrap: 'wrap',
							'@media (min-width:600px)': {
								display: 'flex',
							},
						}}
					>
						<div style={{ display: 'flex', marginRight: '0.5rem' }}>
							<img
								style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', marginRight: '0.5rem' }}
								src={author.userProfileImageUrl || DefaultProfilePng}
								alt="Proflie picture."
							/>
							<div
								style={{
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									fontSize: '0.8rem',
									fontWeight: 'bold',
									justifyContent: 'space-around',
								}}
							>
								<span>{author.username}</span>

								{dataSpaceViewContext.dataSpace.publishedDate && (
									<span>{new Date(dataSpaceViewContext.dataSpace.publishedDate).toLocaleDateString()}</span>
								)}
							</div>
						</div>

						<div style={{ height: 'fit-content', marginTop: '0.5rem' }}>
							{author.website && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										margin: '0 0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(author.website, '_blank')}
								>
									<WebsiteIcon style={{ fontSize: '1.2rem' }} />
								</IconButton>
							)}
							{author.twitter && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										margin: '0 0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(`https://x.com/${author.twitter}`, '_blank')}
								>
									<TwitterIcon style={{ fontSize: '1.2rem' }} />
								</IconButton>
							)}
							{author.reddit && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										margin: '0 0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(`https://www.reddit.com/user/${author.reddit}`, '_blank')}
								>
									<RedditIcon style={{ fontSize: '1.2rem' }} />
								</IconButton>
							)}
							{author.linkedIn && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										margin: '0 0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(`https://www.linkedin.com/in/${author.linkedIn}`, '_blank')}
								>
									<LinkedInIcon style={{ fontSize: '1.2rem' }} />
								</IconButton>
							)}
							{author.instagram && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(`https://www.instagram.com/${author.instagram}`, '_blank')}
								>
									<InstagramIcon style={{ fontSize: '1.2rem' }} />
								</IconButton>
							)}
							{author.tikTok && (
								<IconButton
									style={{
										backgroundColor: '#E9E9E9',
										padding: '0.25rem',
										margin: '0 0.25rem',
										color: '#1C95E0',
									}}
									onClick={() => window.open(`https://www.tiktok.com/@${author.tikTok}`, '_blank')}
								>
									<SvgIcon style={{ fontSize: '1.2rem' }}>
										<TikTokIcon style={{ fontSize: '1.2rem' }} />
									</SvgIcon>
								</IconButton>
							)}
						</div>
					</Box>

					<Typography
						variant="h3"
						sx={{
							display: '-webkit-box',
							lineClamp: '2',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							wordBreak: 'break-all',
							fontSize: '1.4rem',
							color: 'white',
							fontWeight: 'lighter',
							textShadow: '1px 1px 5px rgba(0, 0, 0, 0.4)',
							overflow: 'hidden',
							'@media (min-width:600px)': {
								display: 'none',
							},
						}}
					>
						{dataSpaceViewContext.dataSpace.name}
					</Typography>

					{process.env.REACT_APP_FEATURE_TOGGLE_PASSWORD === localStorage.getItem('featureTogglePassword') ? (
						<Button
							color="success"
							variant="contained"
							style={{ width: 'fit-content', height: 'fit-content', marginLeft: 'auto', color: 'white' }}
							onClick={() => {
								trackEvent({
									userDetails: { userId: userProfile?._id, email: userProfile?.email },
									eventDetails: {
										types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
										eventName: 'WebApp_User clicked Add to My Account button from shared DataSpace.',
									},
								});

								handleAuth();
							}}
						>
							Add to My Account
						</Button>
					) : (
						<Button
							color="success"
							variant="contained"
							style={{ width: 'fit-content', height: 'fit-content', marginLeft: 'auto', color: 'white' }}
							onClick={() => {
								trackEvent({
									userDetails: { userId: userProfile?._id, email: userProfile?.email },
									eventDetails: {
										types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
										eventName: 'WebApp_User clicked Sign Up button from shared DataSpace.',
									},
								});

								// Kissmetrics A/B test to open signup link in new tab or same tab
								window.kmval?.openSignupLink === 'newTab'
									? window.open('/register', '_blank')
									: history.push('/register');
							}}
						>
							Sign Up
						</Button>
					)}
				</div>

				<Typography
					variant="h3"
					sx={{
						position: 'relative',
						zIndex: '2',
						display: 'none',
						marginTop: '0.5rem',
						lineClamp: '2',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						wordBreak: 'break-all',
						fontSize: '2rem',
						color: 'white',
						fontWeight: 'lighter',
						textShadow: '1px 1px 5px rgba(0, 0, 0, 0.4)',
						overflow: 'hidden',
						'@media (min-width:600px)': {
							display: '-webkit-box',
						},
					}}
				>
					{dataSpaceViewContext.dataSpace.name}
				</Typography>
			</div>
		</>
	);
};

export default DataSpaceViewHeader;
